import React, { useEffect, useCallback } from 'react';
import { useDataStore } from '../../providers';
import PublishLoader from './PublishLoader';
import Strings from '../../common/Strings';
import { AppStoreActionTypes } from '../../reducers/AppStore/AppStore.types';
import { UserStoreActionTypes } from '../../reducers';
import { APP_EVENTS } from '../../common/Types';

const Loader = () => {
  const { state, dispatch } = useDataStore();
  const showLoader = state.appState.isLoading;
  const loadingMessage = state.appState.loadingMessage;
  const isPublishSuccess = state.appState.isPublishSuccess;
  const isPublishError = state.appState.isPublishError;
  const publishErrorMessage = state.appState.publishErrorMessage;
  
  // Only show the full-screen loader for publishing operations
  // Generation and enhancement have their own inline loaders in the chat panel
  const isPublishing = loadingMessage === Strings.PUBLISH_LOADER_MESSAGE;

  useEffect(() => {
    // hide scroll on body when publish loader is visible
    if ((showLoader || isPublishSuccess || isPublishError) && isPublishing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showLoader, isPublishing, isPublishSuccess, isPublishError]);

  // Handle success animation completion - transition to homepage
  const handleSuccessComplete = useCallback(() => {
    // Remove loader
    dispatch({ type: AppStoreActionTypes.RemoveLoading });
    // Reset issues
    dispatch({ type: UserStoreActionTypes.ResetIssues });
    // Go to homepage
    dispatch({
      type: AppStoreActionTypes.SetIsHomePage,
      payload: true
    });
    // Emit custom event on story publish
    const event = new CustomEvent(APP_EVENTS.PUBLISH_TO_JIRA);
    document.dispatchEvent(event);
  }, [dispatch]);

  // Handle error animation completion - just dismiss the loader and stay on results page
  const handleErrorComplete = useCallback(() => {
    // Remove loader and error state
    dispatch({ type: AppStoreActionTypes.RemoveLoading });
  }, [dispatch]);

  // Only render the PublishLoader for publishing operations
  // Other loading states (generation, enhancement) are handled inline in the chat panel
  if ((showLoader || isPublishSuccess || isPublishError) && isPublishing) {
    return (
      <PublishLoader
        isVisible={true}
        isSuccess={isPublishSuccess}
        isError={isPublishError}
        title={Strings.PUBLISH_LOADER_TITLE}
        subtitle={Strings.PUBLISH_LOADER_SUBTITLE}
        successTitle={Strings.PUBLISH_SUCCESS_TITLE}
        successSubtitle={Strings.PUBLISH_SUCCESS_SUBTITLE}
        errorTitle={Strings.ERROR_MESSAGES.JIRA_PUBLISH_GENERIC_ERROR.HEADER}
        errorSubtitle={publishErrorMessage || Strings.ERROR_MESSAGES.JIRA_PUBLISH_GENERIC_ERROR.BODY}
        size="lg"
        onSuccessComplete={handleSuccessComplete}
        onErrorComplete={handleErrorComplete}
      />
    );
  }

  // Return null for all other loading states - they have their own inline loaders
  return null;
};

export default Loader;
